import type { CameraOptions } from '@mindcloudxai/shared';
import type { NavigationMode, NavigationModeContext, NavigationModeKey } from './types';
import { isEditableTarget } from './shared';

export class FlyMode implements NavigationMode {
  readonly key: NavigationModeKey = 'fly';

  private ctx: NavigationModeContext | null = null;
  private pos = { x: 0, y: 1.5, z: 4 };
  private yaw = 0;
  private pitch = 0;
  private keys = new Set<string>();
  private isPointerLocked = false;
  private rafId: number | null = null;
  private lastFrame = 0;

  activate(ctx: NavigationModeContext) {
    this.ctx = ctx;
    const el = ctx.canvas;
    this.syncFromCamera(ctx.renderer.getCamera());
    this.isPointerLocked = document.pointerLockElement === el;

    el.addEventListener('click', this.requestPointerLock);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('keyup', this.onKeyUp);

    this.startLoop();
  }

  deactivate() {
    if (!this.ctx) return;
    const el = this.ctx.canvas;
    el.removeEventListener('click', this.requestPointerLock);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);

    if (document.pointerLockElement === el) document.exitPointerLock();
    this.isPointerLocked = false;
    this.keys.clear();
    this.stopLoop();
    this.ctx = null;
  }

  private requestPointerLock = () => {
    if (!this.ctx) return;
    void this.ctx.canvas.requestPointerLock();
  };

  private onPointerLockChange = () => {
    this.isPointerLocked = document.pointerLockElement === this.ctx?.canvas;
  };

  private onMouseMove = (e: MouseEvent) => {
    if (!this.isPointerLocked) return;
    const sensitivity = 0.002;
    this.yaw += e.movementX * sensitivity;
    // FPV 模式：解除俯仰角限制，允许全向旋转（无人机风格）
    this.pitch = this.pitch - e.movementY * sensitivity;
    // 保持 pitch 在 [-π, π] 范围内，避免数值漂移
    this.pitch = Math.atan2(Math.sin(this.pitch), Math.cos(this.pitch));
  };

  private onKeyDown = (e: KeyboardEvent) => {
    if (!this.isPointerLocked && isEditableTarget(e.target)) return;
    this.keys.add(e.code);
  };

  private onKeyUp = (e: KeyboardEvent) => {
    this.keys.delete(e.code);
  };

  private syncFromCamera(camera: CameraOptions) {
    this.pos = { ...camera.position };
    const dx = camera.target.x - camera.position.x;
    const dy = camera.target.y - camera.position.y;
    const dz = camera.target.z - camera.position.z;
    const horizontal = Math.hypot(dx, dz);
    this.yaw = Math.atan2(dx, -dz);
    this.pitch = Math.atan2(dy, horizontal);
  }

  private startLoop() {
    this.lastFrame = performance.now();
    const loop = (now: number) => {
      this.update(now);
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  private stopLoop() {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private update(now: number) {
    if (!this.ctx) return;
    const dt = Math.min((now - this.lastFrame) / 1000, 0.05);
    this.lastFrame = now;
    const sprint = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight');
    const speed = sprint ? 4.8 : 2.4;

    const fwdX = Math.sin(this.yaw);
    const fwdZ = -Math.cos(this.yaw);
    const rightX = Math.cos(this.yaw);
    const rightZ = Math.sin(this.yaw);

    let moveX = 0;
    let moveY = 0;
    let moveZ = 0;

    if (this.keys.has('KeyW') || this.keys.has('ArrowUp')) {
      moveX += fwdX;
      moveZ += fwdZ;
    }
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) {
      moveX -= fwdX;
      moveZ -= fwdZ;
    }
    if (this.keys.has('KeyA') || this.keys.has('ArrowLeft')) {
      moveX -= rightX;
      moveZ -= rightZ;
    }
    if (this.keys.has('KeyD') || this.keys.has('ArrowRight')) {
      moveX += rightX;
      moveZ += rightZ;
    }
    if (this.keys.has('KeyQ')) moveY -= 1;
    if (this.keys.has('KeyE')) moveY += 1;

    const moveLen = Math.hypot(moveX, moveY, moveZ);
    if (moveLen > 0) {
      const distance = speed * dt;
      this.pos.x += (moveX / moveLen) * distance;
      this.pos.y += (moveY / moveLen) * distance;
      this.pos.z += (moveZ / moveLen) * distance;
    }

    const lookX = this.pos.x + Math.sin(this.yaw) * Math.cos(this.pitch);
    const lookY = this.pos.y + Math.sin(this.pitch);
    const lookZ = this.pos.z - Math.cos(this.yaw) * Math.cos(this.pitch);

    // pitch 超过 ±90° 时相机倒置，需翻转 up 向量，否则 Camera.look 中 up∥dir 退化
    const inverted = Math.abs(this.pitch) > Math.PI / 2;
    const up = inverted ? { x: 0, y: -1, z: 0 } : { x: 0, y: 1, z: 0 };

    this.ctx.renderer.setCamera({
      position: { ...this.pos },
      target: { x: lookX, y: lookY, z: lookZ },
      up,
    });
  }
}
